import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  FileText, FlaskConical, PackagePlus, Package, ListChecks, Users, BellRing,
  AlertTriangle, Calendar, ArrowUpRight,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { STATUS_BADGE, formatCurrency } from "@/lib/constants";
import { KpiTile, ActivePoTable, TasksTable, StockTable, DashHeader, MomStrip } from "@/components/DashboardKit";

export default function PurchaseDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notified, setNotified] = useState(() => new Set());
  const { user } = useAuth();

  useEffect(() => {
    let mounted = true;
    api.get("/dashboard/purchase")
      .then((r) => mounted && setData(r.data))
      .catch(() => toast.error("Failed to load purchase dashboard"))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!data) return;
    const batch = [];
    for (const t of data.tasks || []) if (t.overdue) batch.push({ k: `t-${t.id}`, m: `Overdue task: ${t.task_title}` });
    for (const q of data.quotations || []) if (q.overdue) batch.push({ k: `q-${q.id}`, m: `Vendor follow-up overdue: ${q.vendor_name}` });
    const fresh = batch.filter((b) => !notified.has(b.k)).slice(0, 5);
    if (fresh.length) {
      fresh.forEach((n) => toast.warning(n.m, { icon: "⚠️" }));
      setNotified((prev) => { const n = new Set(prev); fresh.forEach((f) => n.add(f.k)); return n; });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (loading) return <div className="p-6 space-y-4"><Skeleton className="h-10 w-80" /><div className="grid grid-cols-4 gap-3">{[1,2,3,4].map(i=><Skeleton key={i} className="h-24" />)}</div><Skeleton className="h-80" /></div>;
  if (!data) return null;
  const s = data.summary || {};

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="purchase-dashboard-page">
      <DashHeader
        eyebrow="Purchase Dashboard"
        title={`Hello ${user?.name?.split(" ")[0]} — your procurement cockpit`}
        hint="Active POs assigned to the purchase team, vendor follow-ups, assigned tasks and live production stock."
        right={<div className="flex gap-2">
          {s.overdue_tasks > 0 && <Badge variant="outline" className="border-red-200 text-red-700 bg-red-50 gap-1"><AlertTriangle className="h-3 w-3" />{s.overdue_tasks} overdue</Badge>}
          {s.pending_quotations > 0 && <Badge variant="outline" className="border-amber-200 text-amber-700 bg-amber-50 gap-1"><BellRing className="h-3 w-3" />{s.pending_quotations} quotations pending</Badge>}
        </div>}
      />

      <MomStrip items={data.mom} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kpi-grid">
        <KpiTile label="Active POs" value={s.active_pos} icon={FileText} accent="blue" testid="kpi-active-pos" />
        <KpiTile label="Pending Quotations" value={s.pending_quotations} sub="Awaiting decision" icon={FlaskConical} accent="amber" testid="kpi-quotations" />
        <KpiTile label="My Pending Tasks" value={s.pending_tasks} sub={`${s.overdue_tasks} overdue · ${s.due_soon_tasks} due soon`} icon={ListChecks} accent="rose" testid="kpi-tasks" />
        <KpiTile label="Materials & Samples" value={`${s.material_pending || 0} / ${s.samples_pending || 0}`} sub="QC pending / sample review" icon={PackagePlus} accent="violet" testid="kpi-mat" />
      </div>

      <Tabs defaultValue="pos" data-testid="purchase-tabs">
        <TabsList className="h-auto flex-wrap bg-slate-100 p-1">
          <TabsTrigger value="pos" data-testid="tab-active-pos"><FileText className="h-3.5 w-3.5 mr-1.5" /> Active PO</TabsTrigger>
          <TabsTrigger value="proc" data-testid="tab-procurement"><Users className="h-3.5 w-3.5 mr-1.5" /> Procurement Details</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="tab-tasks"><ListChecks className="h-3.5 w-3.5 mr-1.5" /> Pending Task</TabsTrigger>
          <TabsTrigger value="stock" data-testid="tab-stock"><Package className="h-3.5 w-3.5 mr-1.5" /> Production Stock</TabsTrigger>
        </TabsList>

        <TabsContent value="pos" data-testid="panel-pos">
          <Card className="border-slate-200"><CardContent className="p-5">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Active / Ongoing Purchase Orders</div>
            <ActivePoTable rows={data.active_pos || []} />
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="proc" className="space-y-4" data-testid="panel-proc">
          {data.vendors.length > 0 && (
            <Card className="border-slate-200"><CardContent className="p-5">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Vendor Follow-ups</div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {data.vendors.map((v) => (
                  <div key={v.vendor} className="border border-slate-200 rounded-md p-3" data-testid={`vendor-${v.vendor}`}>
                    <div className="text-sm font-semibold text-slate-900">{v.vendor}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1">
                      <Calendar className="h-3 w-3" /> Next: {v.next_followup || "—"}
                    </div>
                    <div className="text-xs text-amber-700 mt-1">{v.pending} pending quotation{v.pending > 1 ? "s" : ""}</div>
                  </div>
                ))}
              </div>
            </CardContent></Card>
          )}
          <Card className="border-slate-200"><CardContent className="p-5">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Procurement Quotations</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200"><tr>
                  {["Vendor", "Product", "Quoted Price", "Quotation Date", "Next Follow-up", "Status"].map((h) => (
                    <th key={h} className="text-left py-2.5 px-3 text-[11px] font-semibold uppercase tracking-wider text-slate-600">{h}</th>
                  ))}
                  <th className="py-2.5 px-3 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-600">Open</th>
                </tr></thead>
                <tbody>
                  {data.quotations.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-slate-500">No quotations.</td></tr>}
                  {data.quotations.map((q) => (
                    <tr key={q.id} className={`border-b border-slate-100 hover:bg-slate-50/60 ${q.overdue ? "bg-red-50/40" : ""}`} data-testid={`quot-row-${q.id}`}>
                      <td className="py-2.5 px-3 font-semibold text-slate-900">{q.vendor_name || "—"}</td>
                      <td className="py-2.5 px-3">{q.product || "—"}</td>
                      <td className="py-2.5 px-3 font-mono font-semibold">{formatCurrency(q.quoted_price)}</td>
                      <td className="py-2.5 px-3 text-slate-700">{q.quotation_date || "—"}</td>
                      <td className="py-2.5 px-3">
                        <span className={q.overdue ? "text-red-600 font-semibold" : (q.due_soon ? "text-amber-700" : "text-slate-700")}>
                          {q.next_followup || "—"}{q.overdue && " · OVERDUE"}
                        </span>
                      </td>
                      <td className="py-2.5 px-3"><Badge variant="outline" className={STATUS_BADGE(q.status)}>{q.status}</Badge></td>
                      <td className="py-2.5 px-3 text-right">
                        <Link to={`/m/procurement_quotations/${q.id}`} target="_blank" rel="noreferrer" className="text-blue-600 inline-flex items-center" data-testid={`quot-open-${q.id}`}>
                          Open <ArrowUpRight className="h-3.5 w-3.5 ml-0.5" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="tasks" data-testid="panel-tasks">
          <Card className="border-slate-200"><CardContent className="p-5">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Assigned Tasks</div>
              <div className="flex gap-2">
                <Badge variant="outline" className="border-red-200 text-red-700 bg-red-50">{s.overdue_tasks} overdue</Badge>
                <Badge variant="outline" className="border-amber-200 text-amber-700 bg-amber-50">{s.due_soon_tasks} due 7d</Badge>
              </div>
            </div>
            <TasksTable rows={data.tasks || []} />
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="stock" data-testid="panel-stock">
          <Card className="border-slate-200"><CardContent className="p-5">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Production Stock</div>
            <StockTable rows={data.stocks || []} />
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
